import React, { useMemo, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichText } from "@/components/RichText";
import { ChevronDown, ChevronUp, Copy, GripVertical, Plus, Trash2 } from "lucide-react";
import { NumField, SelectField, TxtField } from "./fields";
import { localizedPatch, QUESTION_TYPES, usesOptions } from "./questionBuilder";
import { buildReversePreview } from "./publicationValidation";

const languageName = (language) => (language === "id" ? "Indonesia" : "English");

function LanguageSwitch({ value, onChange, questionIndex }) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1" role="group" aria-label={`Question ${questionIndex + 1} language`}>
      {[
        { value: "id", label: "Indonesia" },
        { value: "en", label: "English" },
      ].map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          aria-label={`Edit ${option.label} content`}
          data-testid={`q-lang-${option.value}-${questionIndex}`}
          onClick={() => onChange(option.value)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${value === option.value ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function DimensionPicker({ dimensions, selectedIds, onChange, label = "Dimensions", testPrefix }) {
  const selected = selectedIds || [];
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {dimensions.map((dimension) => {
          const isSelected = selected.includes(dimension.id);
          const name = dimension.code || dimension.name_id || dimension.name_en || "Dimension";
          return (
            <button
              key={dimension.id}
              type="button"
              aria-pressed={isSelected}
              aria-label={`${isSelected ? "Remove" : "Assign"} dimension ${name}`}
              onClick={() => onChange(isSelected ? selected.filter((id) => id !== dimension.id) : [...selected, dimension.id])}
              data-testid={`${testPrefix}-${dimension.code || dimension.id}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${isSelected ? "border-terracotta-500 bg-terracotta-500 text-cream-50" : "border-border bg-background hover:border-terracotta-300"}`}
            >
              {name}
            </button>
          );
        })}
        {dimensions.length === 0 && <span className="text-xs text-muted-foreground">Add dimensions first.</span>}
      </div>
    </div>
  );
}

function OptionMarker({ type, order }) {
  if (type === "multiple_choice") return <span aria-hidden="true" className="mt-2 h-4 w-4 shrink-0 rounded border-2 border-muted-foreground/60" />;
  if (type === "dropdown") return <span aria-hidden="true" className="mt-1.5 w-5 shrink-0 text-right font-mono text-xs text-muted-foreground">{order}.</span>;
  return <span aria-hidden="true" className="mt-2 h-4 w-4 shrink-0 rounded-full border-2 border-muted-foreground/60" />;
}

export default function SortableQuestion({
  id,
  q,
  qi,
  dims,
  sections,
  updQuestion,
  rmQuestion,
  duplicateQuestion,
  addOption,
  updOption,
  rmOption,
  moveOption,
  addMatrixRow,
  updMatrixRow,
  rmMatrixRow,
  changeQuestionType,
  scoring,
  lang,
  expanded = false,
  onToggleExpanded,
}) {
  const [activeLanguage, setActiveLanguage] = useState("id");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const showOptions = usesOptions(q.question_type);
  const isForcedChoice = q.question_type === "forced_choice";
  const contentLanguage = languageName(activeLanguage);
  const optionHeading = q.question_type === "matrix" ? "Matrix columns / answer options" : "Answer options";
  const reversePreview = expanded ? buildReversePreview(q, scoring) : null;
  const previewHtml = q[`text_${lang}`] || q.text_id || q.text_en || "";
  const preview = useMemo(() => {
    // Keep markup inert and render only plain text in the sortable shell.
    const template = document.createElement("template");
    template.innerHTML = previewHtml.replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?\s*>/gi, " ");
    template.content.querySelectorAll("script, style, template").forEach((node) => node.remove());
    const text = (template.content.textContent || "").replace(/\s+/g, " ").trim();
    return text.length > 160 ? `${text.slice(0, 160)}…` : text;
  }, [previewHtml]);
  const displayScore = (value) => Number.isInteger(value) ? String(value) : String(Number(value.toFixed(6)));

  return (
    <article ref={setNodeRef} style={style} className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm" data-testid={`q-row-${qi}`}>
      <div className="flex flex-wrap items-center gap-3 border-b border-border bg-muted/20 px-4 py-3 sm:px-5">
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Drag question ${qi + 1}`}
          title="Drag to reorder question"
          className="cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing"
          data-testid={`q-drag-${qi}`}
        >
          <GripVertical className="h-5 w-5" />
        </button>
        <div className="min-w-20">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Question {q.order || qi + 1}</div>
          <div className="font-mono text-xs text-foreground/70">{q.code || "No code"}</div>
        </div>
        <div className="min-w-0 flex-1 basis-48">
          <p className="truncate text-sm" data-testid={`q-preview-${qi}`}>
            {preview || (lang === "id" ? "Belum ada teks pertanyaan" : "No question text")}
          </p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>{QUESTION_TYPES.find((type) => type.value === q.question_type)?.label || q.question_type}</span>
            <span>{(q.options || []).length} {lang === "id" ? "opsi jawaban" : "answer options"}</span>
            {q.reverse_scored && <span>{lang === "id" ? "Skor terbalik" : "Reverse scored"}</span>}
            {q.question_type === "matrix" && q.matrix_rows?.some((row) => row.reverse_scored) && (
              <span>{lang === "id" ? "Baris matriks terbalik" : "Reverse-scored matrix rows"}</span>
            )}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-expanded={expanded}
          aria-controls={`question-editor-${id}`}
          aria-label={`${expanded ? "Collapse" : "Edit"} question ${qi + 1}`}
          onClick={onToggleExpanded}
          data-testid={`q-expand-${qi}`}
        >
          {expanded ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}
          {expanded ? (lang === "id" ? "Tutup" : "Collapse") : "Edit"}
        </Button>
      </div>

      {expanded && (
      <div id={`question-editor-${id}`} data-testid={`q-editor-${qi}`}>
      <div className="space-y-6 p-4 sm:p-5">
        <Select value={q.question_type} onValueChange={(value) => changeQuestionType(qi, value)}>
          <SelectTrigger aria-label={`Question ${qi + 1} type`} data-testid={`q-type-${qi}`} className="h-9 w-full text-sm sm:ml-auto sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {QUESTION_TYPES.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <section className="space-y-3">
          <LanguageSwitch value={activeLanguage} onChange={setActiveLanguage} questionIndex={qi} />
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Question / statement · {contentLanguage}
            </Label>
            <RichText
              value={q[`text_${activeLanguage}`] || ""}
              onChange={(value) => updQuestion(qi, localizedPatch(activeLanguage, "text", value))}
              mode="question"
              minHeight={80}
              placeholder={lang === "id" ? "Tulis pertanyaan atau pernyataan…" : "Write the question or statement…"}
              ariaLabel={`Question ${qi + 1} text in ${contentLanguage}`}
              testid={`q-text-${activeLanguage}-${qi}`}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Description (optional) · {contentLanguage}
            </Label>
            <RichText
              value={q[`description_${activeLanguage}`] || ""}
              onChange={(value) => updQuestion(qi, localizedPatch(activeLanguage, "description", value))}
              mode="description"
              minHeight={80}
              placeholder={lang === "id" ? "Tambahkan petunjuk atau konteks opsional…" : "Add optional guidance or context…"}
              ariaLabel={`Question ${qi + 1} description in ${contentLanguage}`}
              testid={`q-description-${activeLanguage}-${qi}`}
            />
          </div>
        </section>

        {showOptions && (
          <section className="space-y-3">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{optionHeading}</h3>
              <p className="mt-1 text-xs text-muted-foreground">Labels are bilingual. Code and score are shared across both languages.</p>
            </div>
            <div className="space-y-2">
              {(q.options || []).map((option, optionIndex) => (
                <div key={option.id} className="flex items-start gap-3 rounded-xl border border-border/80 bg-background p-3" data-testid={`q-${qi}-opt-${optionIndex}`}>
                  <OptionMarker type={q.question_type} order={optionIndex + 1} />
                  <div className="min-w-0 flex-1 space-y-2.5">
                    <Input
                      aria-label={`Option ${optionIndex + 1} label in ${contentLanguage}`}
                      placeholder={`Answer in ${contentLanguage}`}
                      value={option[`label_${activeLanguage}`] || ""}
                      onChange={(event) => updOption(qi, optionIndex, localizedPatch(activeLanguage, "label", event.target.value))}
                      data-testid={`q-${qi}-opt-${optionIndex}-label-${activeLanguage}`}
                      className="h-10"
                    />
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="w-28 space-y-1">
                        <Label htmlFor={`option-code-${option.id}`} className="text-[10px] uppercase tracking-wider text-muted-foreground">Code</Label>
                        <Input
                          id={`option-code-${option.id}`}
                          aria-label={`Option ${optionIndex + 1} code`}
                          value={option.code || ""}
                          onChange={(event) => updOption(qi, optionIndex, { code: event.target.value })}
                          data-testid={`q-${qi}-opt-${optionIndex}-code`}
                          className="h-8 font-mono text-xs"
                        />
                      </div>
                      <div className="w-28 space-y-1">
                        <Label htmlFor={`option-score-${option.id}`} className="text-[10px] uppercase tracking-wider text-muted-foreground">Score</Label>
                        <Input
                          id={`option-score-${option.id}`}
                          aria-label={`Option ${optionIndex + 1} score`}
                          type="number"
                          step="any"
                          value={option.score ?? 0}
                          onChange={(event) => updOption(qi, optionIndex, { score: event.target.value === "" ? 0 : Number(event.target.value) })}
                          data-testid={`q-${qi}-opt-${optionIndex}-score`}
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="ml-auto flex items-center gap-1">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label={`Move option ${optionIndex + 1} up`}
                          title="Move option up"
                          disabled={optionIndex === 0}
                          onClick={() => moveOption(qi, optionIndex, -1)}
                          className="h-8 w-8"
                          data-testid={`q-${qi}-opt-${optionIndex}-up`}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label={`Move option ${optionIndex + 1} down`}
                          title="Move option down"
                          disabled={optionIndex === (q.options || []).length - 1}
                          onClick={() => moveOption(qi, optionIndex, 1)}
                          className="h-8 w-8"
                          data-testid={`q-${qi}-opt-${optionIndex}-down`}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label={`Delete option ${optionIndex + 1}`}
                          title="Delete option"
                          onClick={() => rmOption(qi, optionIndex)}
                          className="h-8 w-8 text-destructive"
                          data-testid={`q-${qi}-opt-${optionIndex}-remove`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {(q.options || []).length === 0 && <div className="rounded-xl border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">No options yet. Add exactly the response options required by the instrument source.</div>}
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => addOption(qi)} data-testid={`q-add-opt-${qi}`} className="text-terracotta-700">
              <Plus className="mr-1 h-4 w-4" /> {lang === "id" ? "Tambah opsi" : "Add option"}
            </Button>
          </section>
        )}

        {(q.question_type === "matrix" || isForcedChoice) && (
          <section className="space-y-3 border-t border-border pt-5">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{isForcedChoice ? (lang === "id" ? "Pernyataan dalam blok" : "Statements in this block") : "Matrix rows"}</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                {isForcedChoice
                  ? (lang === "id"
                    ? "Responden memilih satu pernyataan paling sesuai (2 poin) dan satu paling tidak sesuai (0 poin); sisanya 1 poin. Hubungkan tiap pernyataan ke dimensi yang berbeda, idealnya 3-4 pernyataan per blok."
                    : "Respondents pick the statement most like them (2 points) and least like them (0 points); the rest get 1 point. Key each statement to a different dimension, ideally 3-4 statements per block.")
                  : "Each row may use its own dimensions and reverse-scoring flag."}
              </p>
            </div>
            <div className="space-y-3">
              {(q.matrix_rows || []).map((row, rowIndex) => (
                <div key={row.id} className="space-y-3 rounded-xl border border-border bg-muted/10 p-3" data-testid={`q-${qi}-row-${rowIndex}`}>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-56 flex-1 space-y-1">
                      <Label htmlFor={`matrix-label-${row.id}`} className="text-[10px] uppercase tracking-wider text-muted-foreground">Row label · {contentLanguage}</Label>
                      <Input
                        id={`matrix-label-${row.id}`}
                        aria-label={`Matrix row ${rowIndex + 1} label in ${contentLanguage}`}
                        value={row[`label_${activeLanguage}`] || ""}
                        onChange={(event) => updMatrixRow(qi, rowIndex, localizedPatch(activeLanguage, "label", event.target.value))}
                        data-testid={`q-${qi}-row-${rowIndex}-label-${activeLanguage}`}
                        className="h-9"
                      />
                    </div>
                    <div className="w-28 space-y-1">
                      <Label htmlFor={`matrix-code-${row.id}`} className="text-[10px] uppercase tracking-wider text-muted-foreground">Code</Label>
                      <Input
                        id={`matrix-code-${row.id}`}
                        aria-label={`Matrix row ${rowIndex + 1} code`}
                        value={row.code || ""}
                        onChange={(event) => updMatrixRow(qi, rowIndex, { code: event.target.value })}
                        className="h-9 font-mono text-xs"
                      />
                    </div>
                    {!isForcedChoice && <div className="flex h-9 items-center gap-2 rounded-lg border border-border bg-background px-3">
                      <Switch
                        id={`matrix-reverse-${row.id}`}
                        aria-label={`Reverse scoring for matrix row ${rowIndex + 1}`}
                        checked={!!row.reverse_scored}
                        onCheckedChange={(checked) => updMatrixRow(qi, rowIndex, { reverse_scored: checked })}
                        data-testid={`q-${qi}-row-${rowIndex}-reverse`}
                      />
                      <Label htmlFor={`matrix-reverse-${row.id}`} className="text-xs">Reverse</Label>
                    </div>}
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label={`Delete matrix row ${rowIndex + 1}`}
                      title="Delete matrix row"
                      onClick={() => rmMatrixRow(qi, rowIndex)}
                      className="h-9 w-9 text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <DimensionPicker
                    dimensions={dims}
                    selectedIds={row.dimension_ids}
                    onChange={(dimensionIds) => updMatrixRow(qi, rowIndex, { dimension_ids: dimensionIds })}
                    label={isForcedChoice ? (lang === "id" ? "Dimensi pernyataan" : "Statement dimensions") : "Row dimensions"}
                    testPrefix={`q-${qi}-row-${rowIndex}-dim`}
                  />
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => addMatrixRow(qi)} data-testid={`q-add-row-${qi}`}>
              <Plus className="mr-1 h-3 w-3" /> {isForcedChoice ? (lang === "id" ? "Tambah pernyataan" : "Add statement") : "Add row"}
            </Button>
          </section>
        )}

        <section className="space-y-4 border-t border-border pt-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Scoring & settings</h3>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <TxtField label="Question code" value={q.code} onChange={(value) => updQuestion(qi, { code: value })} testid={`q-code-${qi}`} />
            <SelectField
              label="Section"
              value={q.section_id || "__none__"}
              onChange={(value) => updQuestion(qi, { section_id: value === "__none__" ? null : value })}
              options={[{ value: "__none__", label: "—" }, ...sections.map((section) => ({ value: section.id, label: section.title_id || section.title_en || "Section" }))]}
              testid={`q-section-${qi}`}
              ariaLabel={`Question ${qi + 1} section`}
            />
            <div className="flex items-end">
              <div className="flex h-10 w-full items-center gap-2 rounded-lg border border-border bg-background px-3">
                <Switch
                  id={`question-reverse-${id}`}
                  aria-label={`Reverse scoring for question ${qi + 1}`}
                  checked={!!q.reverse_scored}
                  onCheckedChange={(checked) => updQuestion(qi, { reverse_scored: checked })}
                  data-testid={`q-reverse-${qi}`}
                />
                <Label htmlFor={`question-reverse-${id}`} className="text-sm">Reverse scoring</Label>
              </div>
            </div>
          </div>

          {q.question_type === "slider" && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <NumField label="Slider min" value={q.slider_min ?? 0} onChange={(value) => updQuestion(qi, { slider_min: value })} testid={`q-slider-min-${qi}`} />
              <NumField label="Slider max" value={q.slider_max ?? 10} onChange={(value) => updQuestion(qi, { slider_max: value })} testid={`q-slider-max-${qi}`} />
              <NumField label="Slider step" value={q.slider_step ?? 1} onChange={(value) => updQuestion(qi, { slider_step: value })} testid={`q-slider-step-${qi}`} />
            </div>
          )}

          {reversePreview && (
            <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-3 text-xs text-teal-900" aria-live="polite" data-testid={`q-reverse-preview-${qi}`}>
              <div className="font-semibold">{lang === "id" ? "Preview reverse scoring" : "Reverse-scoring preview"}</div>
              <div className="mt-1 font-mono text-[11px] text-teal-800">
                {reversePreview.formula}
                {reversePreview.scaleMin !== null && reversePreview.scaleMax !== null
                  ? ` · ${reversePreview.rangeSource === "options" ? (lang === "id" ? "rentang opsi " : "option range ") : ""}${displayScore(reversePreview.scaleMin)}–${displayScore(reversePreview.scaleMax)}`
                  : ""}
              </div>
              {reversePreview.available ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {reversePreview.mappings.map((mapping, mappingIndex) => {
                    const label = mapping[`label_${activeLanguage}`] || mapping.label_id || mapping.label_en || mapping.code || `#${mappingIndex + 1}`;
                    return (
                      <span key={mapping.id || `${mapping.code}-${mappingIndex}`} className="rounded-full border border-teal-200 bg-background px-2 py-1" data-testid={`q-reverse-map-${qi}-${mappingIndex}`}>
                        {label}: <strong>{displayScore(mapping.score)} → {displayScore(mapping.reversedScore)}</strong>
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-2 text-amber-800">
                  {reversePreview.reason === "invalid_scale"
                    ? (lang === "id" ? "Atur skala minimum dan maksimum yang valid di tab Scoring." : "Set a valid minimum and maximum in the Scoring tab.")
                    : (lang === "id" ? "Tambahkan skor opsi numerik untuk melihat pemetaan." : "Add numeric option scores to see the mapping.")}
                </div>
              )}
              {reversePreview.reverseRows.length > 0 && (
                <div className="mt-2 text-[11px]">
                  {lang === "id" ? "Baris matriks terbalik" : "Reversed matrix rows"}: {reversePreview.reverseRows.join(", ")}
                </div>
              )}
              {reversePreview.aggregateMultipleChoice && (
                <div className="mt-2 rounded-lg bg-amber-100 px-2 py-1.5 text-amber-900">
                  {lang === "id"
                    ? "Multiple choice membalik jumlah skor semua opsi terpilih satu kali. Pemetaan di atas menunjukkan skor opsi individual."
                    : "Multiple choice reverses the sum of all selected option scores once. The mapping above shows individual option scores."}
                </div>
              )}
            </div>
          )}

          <DimensionPicker
            dimensions={dims}
            selectedIds={q.dimension_ids}
            onChange={(dimensionIds) => updQuestion(qi, { dimension_ids: dimensionIds })}
            testPrefix={`q-dim-${qi}`}
          />
        </section>
      </div>

      <footer className="flex flex-wrap items-center gap-2 border-t border-border bg-muted/20 px-4 py-3 sm:px-5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`Duplicate question ${qi + 1}`}
          onClick={() => duplicateQuestion(qi)}
          data-testid={`q-duplicate-${qi}`}
        >
          <Copy className="mr-1.5 h-4 w-4" /> Duplicate
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`Delete question ${qi + 1}`}
          onClick={() => rmQuestion(qi)}
          data-testid={`q-remove-${qi}`}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="mr-1.5 h-4 w-4" /> Delete
        </Button>
        <div className="ml-auto flex items-center gap-2 border-l border-border pl-4">
          <Label htmlFor={`question-required-${id}`} className="text-sm">Required</Label>
          <Switch
            id={`question-required-${id}`}
            aria-label={`Required question ${qi + 1}`}
            checked={!!q.required}
            onCheckedChange={(checked) => updQuestion(qi, { required: checked })}
            data-testid={`q-required-${qi}`}
          />
        </div>
      </footer>
      </div>
      )}
    </article>
  );
}
